// Ask AI dashboard chat — right-side panel on the portfolio dashboard,
// styled/behaved like the Drawings/Specs docs-chat panel: purple
// header, AI avatar bubbles, suggestion pills, inline clear-confirm,
// and on desktop the page content is pushed left so the dashboard
// stays fully visible next to the chat.
//
// The controller scope (set in `companies/dashboard.html.erb`) wraps
// BOTH the `portfolio-dashboard-content` turbo-frame and the panel, so
// the "Ask AI" button inside the frame keeps working after every frame
// refresh while the panel + conversation live outside the frame.
//
// Flow per message: append the user's bubble, POST the running
// conversation to `/dashboard_ai_chat`, append the assistant reply,
// and — when the server applied layout actions — refresh the dashboard
// turbo-frame in place, then ring-highlight the changed cards.

import { Controller } from "@hotwired/stimulus"

const MAX_HISTORY = 16

export default class extends Controller {
  static targets = ["panel", "messages", "input", "sendButton", "emptyState", "clearConfirm",
                    "confirmModal", "confirmDefaultButton", "defaultName",
                    "fileInput", "attachments"]
  static values = {
    chatUrl: String,
    defaultUrl: String,
    csrfToken: String,
    frameId: { type: String, default: "portfolio-dashboard-content" },
    partialNote: String,
    defaultSaved: String,
    addedBadge: { type: String, default: "Added by Ask AI" },
    updatedBadge: { type: String, default: "Updated by Ask AI" }
  }

  // Panel is 400px wide (inline style in the partial) + a small gap.
  PANEL_GAP = "412px"

  connect() {
    this._history = []
    this._pending = false
    this._pendingHighlight = null
    // Reference screenshots queued for the next message:
    // [{ dataUrl }] — downscaled JPEGs, sent inline to the AI.
    this._pendingImages = []
    this._boundEscape = (e) => {
      if (e.key === "Escape") this.close()
    }
    // Highlight AI-changed cards after the dashboard turbo-frame
    // refresh lands (the frame content is replaced, so we re-find the
    // widgets by key on every load).
    this._boundFrameLoad = (e) => this._onFrameLoad(e)
    document.addEventListener("turbo:frame-load", this._boundFrameLoad)

    // Mouse-wheel anywhere over the panel scrolls the conversation —
    // never the dashboard behind it (mirrors docs-chat).
    this._onPanelWheel = (e) => {
      e.preventDefault()
      const unit = e.deltaMode === 1 ? 16 : 1
      this.messagesTarget.scrollTop += e.deltaY * unit
    }
    if (this.hasPanelTarget) this.panelTarget.addEventListener("wheel", this._onPanelWheel, { passive: false })
  }

  disconnect() {
    document.removeEventListener("keydown", this._boundEscape)
    document.removeEventListener("turbo:frame-load", this._boundFrameLoad)
    if (this.hasPanelTarget) this.panelTarget.removeEventListener("wheel", this._onPanelWheel)
  }

  // ── Panel open / close ───────────────────────────────────────────

  toggle(event) {
    event?.preventDefault()
    if (!this.hasPanelTarget) return
    if (this.panelTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open(event) {
    event?.preventDefault()
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.remove("hidden")
    this.panelTarget.classList.add("flex")
    // Push the page content left (desktop only — on small screens the
    // panel overlays full-width instead, there's no room to split).
    if (window.matchMedia("(min-width: 1024px)").matches) {
      this.element.style.transition = "padding-right 250ms ease"
      this.element.style.paddingRight = this.PANEL_GAP
    }
    document.addEventListener("keydown", this._boundEscape)
    this._scroll()
    if (this.hasInputTarget) this.inputTarget.focus()
  }

  close(event) {
    event?.preventDefault()
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.add("hidden")
    this.panelTarget.classList.remove("flex")
    this.element.style.paddingRight = ""
    document.removeEventListener("keydown", this._boundEscape)
    this._cancelClearStrip()
    this._closeConfirmModal()
  }

  // ── Composer ─────────────────────────────────────────────────────

  useSuggestion(event) {
    event?.preventDefault()
    if (!this.hasInputTarget) return
    this.inputTarget.value = event.currentTarget.dataset.q || event.currentTarget.textContent.trim()
    this.send()
  }

  // ── Reference screenshots (chart examples for the AI) ────────────

  MAX_IMAGES = 3

  attachImages(event) {
    event?.preventDefault()
    if (this.hasFileInputTarget) this.fileInputTarget.click()
  }

  filesChosen(event) {
    const files = Array.from(event.target?.files || [])
    event.target.value = ""
    this._addImageFiles(files)
  }

  // Pasting a screenshot straight into the input attaches it.
  handlePaste(event) {
    const items = Array.from(event.clipboardData?.items || [])
    const files = items.filter((i) => i.kind === "file" && i.type.startsWith("image/"))
      .map((i) => i.getAsFile()).filter(Boolean)
    if (!files.length) return
    event.preventDefault()
    this._addImageFiles(files)
  }

  _addImageFiles(files) {
    files.forEach((file) => {
      if (!file.type.startsWith("image/")) return
      if (this._pendingImages.length >= this.MAX_IMAGES) return
      this._downscale(file).then((dataUrl) => {
        if (!dataUrl || this._pendingImages.length >= this.MAX_IMAGES) return
        this._pendingImages.push({ dataUrl })
        this._renderAttachments()
      }).catch(() => {})
    })
  }

  // Downscale to ≤1280px JPEG so payloads stay small — screenshots
  // compress very well and Gemini doesn't need full resolution.
  _downscale(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file)
      const img = new Image()
      img.onload = () => {
        try {
          const max = 1280
          const scale = Math.min(1, max / Math.max(img.width, img.height))
          const canvas = document.createElement("canvas")
          canvas.width = Math.max(1, Math.round(img.width * scale))
          canvas.height = Math.max(1, Math.round(img.height * scale))
          const ctx = canvas.getContext("2d")
          ctx.fillStyle = "#ffffff"
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
          resolve(canvas.toDataURL("image/jpeg", 0.85))
        } catch (err) {
          reject(err)
        } finally {
          URL.revokeObjectURL(url)
        }
      }
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("bad image")) }
      img.src = url
    })
  }

  removeAttachment(event) {
    event?.preventDefault()
    const idx = parseInt(event.currentTarget.dataset.index, 10)
    if (Number.isInteger(idx)) this._pendingImages.splice(idx, 1)
    this._renderAttachments()
  }

  _renderAttachments() {
    if (!this.hasAttachmentsTarget) return
    if (!this._pendingImages.length) {
      this.attachmentsTarget.classList.add("hidden")
      this.attachmentsTarget.innerHTML = ""
      return
    }
    this.attachmentsTarget.classList.remove("hidden")
    this.attachmentsTarget.innerHTML = this._pendingImages.map((img, idx) => `
      <div class="relative">
        <img src="${img.dataUrl}" class="w-12 h-12 object-cover rounded-md border border-gray-200" alt="">
        <button type="button" data-action="dashboard-ai-chat#removeAttachment" data-index="${idx}"
                class="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-gray-600 hover:bg-gray-800 text-white text-[10px] leading-none flex items-center justify-center cursor-pointer">&times;</button>
      </div>
    `).join("")
  }

  async send(event) {
    event?.preventDefault()
    if (this._pending) return
    const text = (this.hasInputTarget ? this.inputTarget.value : "").trim()
    const images = this._pendingImages.slice()
    if (!text && !images.length) return

    this._hideEmptyState()
    this.inputTarget.value = ""
    this._pendingImages = []
    this._renderAttachments()

    // History keeps a text marker for past images (they only upload
    // once, on the turn they're attached).
    const marker = images.length ? ` [${images.length} reference image${images.length > 1 ? "s" : ""} attached]` : ""
    this._history.push({ role: "user", content: (text || "Please look at the attached image.") + marker })
    this._history = this._history.slice(-MAX_HISTORY)
    this._appendUser(text, images)

    const thinking = this._appendThinking()
    this._setPending(true)

    try {
      const res = await fetch(this.chatUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({
          messages: this._history,
          images: images.map((img) => ({
            mime_type: "image/jpeg",
            data: (img.dataUrl.split(",")[1] || "")
          }))
        })
      })
      const data = await res.json()
      thinking.remove()

      const reply = data.reply || "Sorry — something went wrong. Please try again."
      this._history.push({ role: "assistant", content: reply })
      this._history = this._history.slice(-MAX_HISTORY)
      this._appendAi(reply)

      if (data.success && Array.isArray(data.skipped) && data.skipped.length > 0 && this.partialNoteValue) {
        this._appendNote(this.partialNoteValue)
      }
      if (data.success && data.layout_changed) {
        this._pendingHighlight = Array.isArray(data.affected_widgets) ? data.affected_widgets : []
        this._refreshDashboard()
      }
    } catch (err) {
      thinking.remove()
      this._appendAi("Sorry — network error. Please try again.")
    } finally {
      this._setPending(false)
    }
  }

  // ── Clear chat (local conversation) ──────────────────────────────

  askClear(event) {
    event?.preventDefault()
    if (this.hasClearConfirmTarget) this.clearConfirmTarget.classList.remove("hidden")
  }

  cancelClear(event) {
    event?.preventDefault()
    this._cancelClearStrip()
  }

  clearChat(event) {
    event?.preventDefault()
    this._history = []
    this._pendingImages = []
    this._renderAttachments()
    // Keep the greeting + suggestion pills (marked data-permanent),
    // drop everything the conversation appended.
    Array.from(this.messagesTarget.children).forEach((el) => {
      if (!el.hasAttribute("data-permanent")) el.remove()
    })
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
    this._cancelClearStrip()
    this._scroll()
  }

  _cancelClearStrip() {
    if (this.hasClearConfirmTarget) this.clearConfirmTarget.classList.add("hidden")
  }

  // ── Company default ──────────────────────────────────────────────

  openDefaultModal(event) {
    event?.preventDefault()
    if (this.hasConfirmModalTarget) this.confirmModalTarget.classList.remove("hidden")
  }

  closeDefaultModal(event) {
    if (event && event.target !== event.currentTarget) return
    this._closeConfirmModal()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async confirmDefault(event) {
    event?.preventDefault()
    if (this.hasConfirmDefaultButtonTarget) this.confirmDefaultButtonTarget.disabled = true

    try {
      const res = await fetch(this.defaultUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ name: this.hasDefaultNameTarget ? this.defaultNameTarget.value : "" })
      })
      const data = await res.json()
      if (data.success) {
        this._closeConfirmModal()
        const message = this.defaultSavedValue || "Saved as the company default."
        this._showToast(message, "notice")
        this._appendNote(message)
      } else {
        this._showToast((data.errors || [data.error || "Save failed"]).join(", "), "alert")
      }
    } catch (err) {
      this._showToast("Network error. Please try again.", "alert")
    } finally {
      if (this.hasConfirmDefaultButtonTarget) this.confirmDefaultButtonTarget.disabled = false
    }
  }

  _closeConfirmModal() {
    if (this.hasConfirmModalTarget) this.confirmModalTarget.classList.add("hidden")
  }

  // ── Rendering (docs-chat bubble style) ───────────────────────────

  _hideEmptyState() {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
  }

  _appendUser(text, images = []) {
    const row = document.createElement("div")
    row.className = "flex gap-2 justify-end"
    const bubble = document.createElement("div")
    bubble.className = "bg-purple-600 text-white rounded-lg rounded-tr-none px-3 py-2 text-[13px] max-w-[270px] whitespace-pre-wrap break-words space-y-1.5"

    if (images.length) {
      const strip = document.createElement("div")
      strip.className = "flex flex-wrap gap-1"
      images.forEach((img) => {
        const thumb = document.createElement("img")
        thumb.src = img.dataUrl
        thumb.alt = ""
        thumb.className = "w-20 h-20 object-cover rounded-md border border-purple-400"
        strip.appendChild(thumb)
      })
      bubble.appendChild(strip)
    }
    if (text) {
      const p = document.createElement("div")
      p.textContent = text
      bubble.appendChild(p)
    }

    row.appendChild(bubble)
    this.messagesTarget.appendChild(row)
    this._scroll()
    return row
  }

  _appendAi(text) {
    const row = document.createElement("div")
    row.className = "flex gap-2"
    const avatar = document.createElement("div")
    avatar.className = "w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[11px] shrink-0"
    avatar.textContent = "AI"
    const bubble = document.createElement("div")
    bubble.className = "bg-gray-100 rounded-lg rounded-tl-none px-3 py-2 text-[13px] text-gray-700 whitespace-pre-wrap break-words"
    bubble.textContent = text
    row.appendChild(avatar)
    row.appendChild(bubble)
    this.messagesTarget.appendChild(row)
    this._scroll()
    return row
  }

  _appendThinking() {
    const row = document.createElement("div")
    row.className = "flex gap-2"
    row.innerHTML = `<div class="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[11px] shrink-0">AI</div>
      <div class="bg-gray-100 rounded-lg rounded-tl-none px-3 py-2 text-[13px] text-gray-400 italic">${this._escape(this._thinkingLabel())}</div>`
    this.messagesTarget.appendChild(row)
    this._scroll()
    return row
  }

  _appendNote(text) {
    const note = document.createElement("p")
    note.className = "text-[11px] text-gray-400 text-center"
    note.textContent = text
    this.messagesTarget.appendChild(note)
    this._scroll()
  }

  _thinkingLabel() {
    return this.element.getAttribute("data-dashboard-ai-chat-thinking-label") || "Thinking…"
  }

  _scroll() {
    if (this.hasMessagesTarget) this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  _setPending(on) {
    this._pending = on
    if (this.hasSendButtonTarget) this.sendButtonTarget.disabled = on
    if (this.hasInputTarget) this.inputTarget.disabled = on
    if (!on && this.hasInputTarget) this.inputTarget.focus()
  }

  // ── Change highlight after the frame refresh ─────────────────────
  //
  // The chat response lists the widgets it touched ({key, change});
  // once the refreshed frame lands, each card gets a purple ring, a
  // soft wash, and an explicit "Added by Ask AI" / "Updated by Ask AI"
  // badge for a few seconds, and the first one scrolls into view — so
  // the user always knows exactly what the AI changed.
  _onFrameLoad(event) {
    if (event.target?.id !== this.frameIdValue) return
    const affected = this._pendingHighlight
    this._pendingHighlight = null
    if (!affected || !affected.length) return

    requestAnimationFrame(() => {
      const ringClasses = ["ring-2", "ring-purple-400", "ring-offset-2", "rounded-xl"]
      let first = null
      const marked = []

      affected.forEach((item) => {
        const key = typeof item === "string" ? item : item?.key
        if (!key || !/^[\w-]+$/.test(key)) return
        const badgeText = (typeof item === "object" && item?.change === "updated")
          ? this.updatedBadgeValue
          : this.addedBadgeValue

        this.element.querySelectorAll(`[data-widget-type="${key}"]`).forEach((el) => {
          if (!first) first = el
          el.classList.add(...ringClasses)

          const marker = document.createElement("div")
          marker.className = "absolute inset-0 pointer-events-none rounded-xl bg-purple-500/5 z-10 transition-opacity duration-500"
          const badge = document.createElement("span")
          badge.className = "absolute -top-2.5 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-600 text-white text-[11px] font-medium shadow"
          badge.textContent = badgeText
          marker.appendChild(badge)
          el.appendChild(marker)
          marked.push({ el, marker })
        })
      })

      if (first) first.scrollIntoView({ behavior: "smooth", block: "center" })

      setTimeout(() => {
        marked.forEach(({ marker }) => marker.classList.add("opacity-0"))
      }, 3800)
      setTimeout(() => {
        marked.forEach(({ el, marker }) => {
          marker.remove()
          el.classList.remove(...ringClasses)
        })
      }, 4400)
    })
  }

  // ── Dashboard refresh (turbo-frame, no full reload) ──────────────

  _refreshDashboard() {
    const frame = document.getElementById(this.frameIdValue)
    if (!frame) {
      window.location.reload()
      return
    }
    if (frame.src) {
      if (typeof frame.reload === "function") {
        frame.reload()
      } else {
        const src = frame.src
        frame.removeAttribute("src")
        frame.src = src
      }
    } else {
      frame.src = window.location.href
    }
  }

  // ── Toast (flash pattern) ────────────────────────────────────────

  _showToast(message, type = "notice") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // ── Utils ────────────────────────────────────────────────────────

  _jsonHeaders() {
    return {
      "Accept": "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": this.csrfTokenValue || (document.querySelector('meta[name="csrf-token"]')?.content || "")
    }
  }

  _escape(str) {
    return String(str || "").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }
}
